// externals
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { DefaultTheme as Theme } from 'styled-components';

// libraries
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { getThemeOverride } from '@makemydeal/dr-shared-ui-utils';

// utils
import { getThemeModule } from './themeUtils';

// interfaces/types
import type { CombinedTheme, FullCombinedTheme } from '../types/CombinedTheme';
import type { InterstateThemeData } from './interstateThemeTypes';

const THEME_OVERRIDES = {
    itemBoxCheckedColor: '#B3B4B7'
};

export const useAppTheme = (): CombinedTheme => {
    const overiddenTheme = getThemeOverride();

    const dealerTheme = useSelector((state: Record<string, any>) => {
        return overiddenTheme || state.dealer?.theme || 'default';
    });

    const interstateTheme = useInterstateTheme() as unknown as InterstateThemeData;
    const [uiBricksTheme, setUiBricksTheme] = useState<Theme>({});
    const [currentTheme, setCurrentTheme] = useState(overiddenTheme || 'default');

    // Initialize combinedTheme directly from interstateTheme so it is non-null on the
    // very first render — tokens like BaseColorBlue700 live in the interstate theme and
    // are available synchronously, so there is no reason to wait for a useEffect cycle.
    // The uiBricks theme (loaded async from the network) is merged in once it arrives.
    const [combinedTheme, setCombinedTheme] = useState<FullCombinedTheme>(
        interstateTheme
            ? ({ ...interstateTheme, uiBricks: {}, ...THEME_OVERRIDES } as FullCombinedTheme)
            : ({ ...THEME_OVERRIDES } as FullCombinedTheme)
    );

    // Update current theme when dealer theme changes
    useEffect(() => {
        setCurrentTheme(dealerTheme);
    }, [dealerTheme]);

    // Update combined theme when interstate theme changes
    useEffect(() => {
        setCombinedTheme({
            ...interstateTheme,
            ...uiBricksTheme,
            uiBricks: uiBricksTheme,
            ...THEME_OVERRIDES
        } as FullCombinedTheme);
    }, [interstateTheme, uiBricksTheme]);

    // Load UI Bricks theme when current theme changes.
    // Note: interstateTheme is intentionally excluded from the dependency array —
    // getThemeModule only needs the theme name string. Including interstateTheme
    // (an object reference that may change identity on re-renders) caused a
    // redundant duplicate network fetch for the same theme JSON.
    // The interstateTheme merge happens in the separate effect above.
    useEffect(() => {
        getThemeModule(currentTheme)
            .then((data: Theme) => {
                setUiBricksTheme(data);
                setCombinedTheme((prev) => ({ ...prev, ...data, uiBricks: data, ...THEME_OVERRIDES } as FullCombinedTheme));
            })
            .catch((error: Error) => {
                // eslint-disable-next-line no-console
                console.error(`Error during loading of ${currentTheme} theme token: ${error}`);
            });
    }, [currentTheme]);

    // interstateTheme is always available synchronously (it comes from React context set
    // by InterstateThemeProvider in sales-checkin.tsx), so combinedTheme is never empty
    // on the first render. Returning combinedTheme directly avoids the null → non-null
    // transition that caused a blank flash between auth completing and Routes rendering.
    return combinedTheme;
};
